/**
 * Macro #20 §18/§19 — Owner Moderation Banner.
 *
 * A single, visually-native (chamfer/HUD, restrained coral glow) banner surfaced
 * on ALL owner surfaces (Dashboard, Parcel/Territory, Artwork, Canvas Editor). It
 * consumes the canonical `useModerationTarget` state — never a per-surface copy.
 *
 * Owner affordances: read the case (reason + Admin Note + status), Request Review,
 * and a "Need help? Request Support" thread. Admin identities / audit internals
 * are NEVER exposed to the owner.
 */
import { useState } from "react";
import { moderationApi, ModerationApiError, type OwnerCase, type SupportMessage } from "../../lib/moderation-api";
import { useModerationTarget } from "./useModerationTarget";
import { notifyModerationRead } from "../useModerationNotifications";
import type { TargetType } from "../../lib/moderation-api";
import { cn } from "../cn";

export interface OwnerModerationBannerProps {
  targetType: TargetType | null;
  targetId: string | null;
  /** Skip the fetch entirely (e.g. viewer is not the owner of this parcel). */
  enabled?: boolean;
  className?: string;
}

export function OwnerModerationBanner({
  targetType,
  targetId,
  enabled = true,
  className,
}: OwnerModerationBannerProps) {
  const { state, refetch } = useModerationTarget(targetType, targetId, enabled);
  if (state.status !== "moderated") return null;
  return (
    <BannerBody
      className={className}
      c={state.view.case!}
      thread={state.view.support_thread}
      ownerUnread={!!state.view.owner_unread}
      onChanged={refetch}
    />
  );
}

function BannerBody({
  c,
  thread,
  ownerUnread,
  onChanged,
  className,
}: {
  c: OwnerCase;
  thread: SupportMessage[];
  ownerUnread: boolean;
  onChanged: () => void;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [draft, setDraft] = useState("");
  // WO-VRD (Macro #20 b) — a case that no longer suppresses visibility
  // (RESTORED / closed) surfaced ONLY because it still holds an unread admin
  // message. Read-only: no Request Review, no send box, just read + clear.
  const closed = c.active === false;
  const [read, setRead] = useState(false);
  const showDot = ownerUnread && !read;

  const reviewPending = c.status === "REVIEW_PENDING";

  // Owner opened the thread → mark the whole target read so BOTH the top-bar
  // glow AND the per-Territory badge clear at once (Macro #20 luminous badge).
  // Fires for ANY open (even an empty thread from a bare moderation action, c)
  // so the badge that "leads the way" to the messaging always clears on arrival.
  const openSupport = () => {
    setSupportOpen((v) => {
      const next = !v;
      if (next && showDot) {
        setRead(true);
        moderationApi
          .markOwnerRead(c.case_id)
          .then(() => notifyModerationRead())
          .catch(() => {});
      }
      return next;
    });
  };

  const requestReview = async () => {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await moderationApi.requestReview(c.case_id);
      setNotice("Review requested. An administrator has been notified.");
      onChanged();
    } catch (e) {
      setErr(e instanceof ModerationApiError ? e.message : "Could not request a review.");
    } finally {
      setBusy(false);
    }
  };

  const sendSupport = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setErr(null);
    try {
      await moderationApi.ownerSupport(c.case_id, body);
      setDraft("");
      onChanged();
    } catch (e) {
      setErr(e instanceof ModerationApiError ? e.message : "Could not send your message.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="owner-moderation-banner"
      data-case-id={c.case_id}
      data-status={c.status}
      className={cn("panel-chamfer panel-chamfer--sm relative select-text", className)}
      style={{
        // Restrained coral accent (native to The Grid palette) — not a generic red alert box.
        boxShadow:
          "inset 0 0 0 1px var(--neon-coral-ring), 0 0 26px -12px var(--neon-coral-glow)",
      }}
    >
      <span className="corner-ticks" style={{ color: "var(--neon-coral)" }} aria-hidden>
        <span className="ct-a" />
        <span className="ct-b" />
      </span>
      <div className="px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--neon-coral)", boxShadow: "0 0 8px var(--neon-coral-glow)" }}
          />
          <span
            data-testid="omb-title"
            className="font-display text-[13px] font-semibold tracking-[0.02em]"
            style={{ color: "var(--neon-coral)" }}
          >
            {closed ? "Message from Admin" : "Moderation Required"}
          </span>
          {!closed && c.emergency && (
            <span
              data-testid="omb-emergency"
              className="ml-1 inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.16em]"
              style={{ color: "var(--neon-coral)", borderColor: "var(--neon-coral-ring)" }}
            >
              Emergency
            </span>
          )}
          {!closed && reviewPending && (
            <span
              data-testid="omb-review-pending"
              className="ml-auto inline-flex items-center rounded-sm border border-border-accent bg-neon-teal-soft px-2 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.16em] text-neon-teal"
            >
              Review Pending
            </span>
          )}
        </div>

        <p className="mt-2 text-[12px] leading-relaxed text-text-secondary">
          {closed
            ? "An administrator sent you a message about this content. Your content is not hidden."
            : "This content is currently hidden from public view."}
        </p>

        <dl className="mt-3 space-y-1.5">
          <Row label="Reason">
            <span data-testid="omb-reason" className="text-text-primary">
              {c.reason_label ?? c.reason_category}
            </span>
          </Row>
          {c.admin_note && (
            <Row label="Admin Note">
              <span data-testid="omb-admin-note" className="text-text-secondary whitespace-pre-wrap break-words">
                {c.admin_note}
              </span>
            </Row>
          )}
          <Row label="Case">
            <span data-testid="omb-case-id" className="font-mono text-[10px] text-text-muted break-all">
              {c.case_id}
            </span>
          </Row>
        </dl>

        {err && (
          <div data-testid="omb-error" className="mt-3 text-[11px] font-mono text-state-danger">
            {err}
          </div>
        )}
        {notice && (
          <div data-testid="omb-notice" className="mt-3 text-[11px] font-mono text-neon-teal">
            {notice}
          </div>
        )}

        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          {!closed && (
            <button
              type="button"
              data-testid="omb-request-review"
              onClick={requestReview}
              disabled={busy || reviewPending}
              className={cn(
                "inline-flex items-center h-8 px-3.5 rounded-md font-mono text-[10.5px] font-medium uppercase tracking-[0.16em]",
                "border transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                "text-neon-coral border-[color:var(--neon-coral-ring)] hover:bg-[color:var(--neon-coral-soft)]",
              )}
            >
              {reviewPending ? "Review Requested" : "Request Review"}
            </button>
          )}
          <button
            type="button"
            data-testid="omb-toggle-support"
            data-unread={showDot}
            onClick={openSupport}
            aria-expanded={supportOpen}
            className={cn(
              "relative inline-flex items-center gap-2 h-8 px-3.5 rounded-md font-mono text-[10.5px] font-medium uppercase tracking-[0.16em] transition-colors",
              showDot
                ? "text-neon-coral border border-[color:var(--neon-coral-ring)] bg-[color:var(--neon-coral-soft)]"
                : "text-text-secondary border border-border-soft hover:text-neon-teal hover:border-border-accent",
            )}
          >
            {showDot && (
              <span
                data-testid="omb-support-unread-dot"
                className="notif-glow-dot h-2 w-2 shrink-0"
                aria-hidden
              />
            )}
            {closed
              ? "Read admin message"
              : showDot
                ? "New message · Open Support"
                : "Need help? Request Support"}
          </button>
        </div>

        {supportOpen && (
          <div data-testid="omb-support" className="mt-3 rounded-md border border-border-soft bg-obsidian/50 p-3">
            <ul data-testid="omb-support-thread" className="space-y-2 max-h-52 overflow-y-auto scroll-dark">
              {thread.length === 0 && (
                <li className="text-[11px] text-text-muted font-mono">No messages yet. Start the conversation below.</li>
              )}
              {thread.map((m) => (
                <li key={m.message_id} data-testid="omb-support-message" data-role={m.sender_role} className="text-[12px]">
                  <span
                    className={cn(
                      "font-mono text-[8.5px] uppercase tracking-[0.16em] mr-2",
                      m.sender_role === "admin" ? "text-neon-teal" : "text-text-muted",
                    )}
                  >
                    {m.sender_role === "admin" ? "Admin" : "You"}
                  </span>
                  <span className="text-text-secondary whitespace-pre-wrap break-words">{m.body}</span>
                </li>
              ))}
            </ul>
            {!closed && (
              <div className="mt-2 flex items-end gap-2">
                <textarea
                  data-testid="omb-support-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={2}
                  placeholder="Describe your issue…"
                  className="flex-1 resize-none rounded-md border border-border-soft bg-obsidian px-2.5 py-2 text-[12px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-accent"
                />
                <button
                  type="button"
                  data-testid="omb-support-send"
                  onClick={sendSupport}
                  disabled={busy || !draft.trim()}
                  className="inline-flex items-center h-8 px-3.5 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-neon-teal border border-border-accent hover:bg-neon-teal-soft disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Send
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-24 shrink-0 font-mono text-[8.5px] uppercase tracking-[0.18em] text-text-muted pt-0.5">
        {label}
      </dt>
      <dd className="m-0 flex-1 text-[12px]">{children}</dd>
    </div>
  );
}
